import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus,
  Search,
  Filter,
  Edit,
  Eye,
  Trash2,
  BookOpen,
  Calendar,
  User,
  Tag,
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Star,
  X,
  Save,
  Globe,
  Image,
  Clock
} from 'lucide-react';
import { BASE_URL } from '../config';

export default function Blog({ onBack, onNavigate }) {
  const [blogPosts, setBlogPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [selectedReadingTime, setSelectedReadingTime] = useState('');
  const [notification, setNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedPosts, setSelectedPosts] = useState([]);

  // Category management state
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    id: null,
    name: '',
    slug: '',
    description: '',
    color: '#3B82F6',
    is_active: true
  });
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' },
    { value: 'archived', label: 'Archived' }
  ];

  // Helper function to safely split tags
  const getTagsArray = (tags) => {
    // If tags is already an array (from backend tags_list), return it
    if (Array.isArray(tags)) {
      return tags.filter(tag => tag && tag.trim().length > 0);
    }
    // If tags is a string, split it
    if (!tags || typeof tags !== 'string') return [];
    return tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
  };

  // Helper function to safely format dates
  const safeFormatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return formatDate(dateString);
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Category management functions
  const openCategoryModal = (category = null) => {
    if (category) {
      setCategoryForm({
        id: category.id,
        name: category.name,
        slug: category.slug,
        description: category.description,
        color: category.color,
        is_active: category.is_active
      });
      setEditingCategory(category);
    } else {
      setCategoryForm({
        id: null,
        name: '',
        slug: '',
        description: '',
        color: '#3B82F6',
        is_active: true
      });
      setEditingCategory(null);
    }
    setShowCategoryForm(true);
  };

  const closeCategoryModal = () => {
    setShowCategoryModal(false);
    setShowCategoryForm(false);
    setCategoryForm({
      id: null,
      name: '',
      slug: '',
      description: '',
      color: '#3B82F6',
      is_active: true
    });
    setEditingCategory(null);
  };

  const handleCategoryFormChange = (field, value) => {
    setCategoryForm(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-generate slug from name
    if (field === 'name') {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setCategoryForm(prev => ({
        ...prev,
        slug: slug
      }));
    }
  };

  const saveBlogCategory = async () => {
    if (!categoryForm.name.trim()) {
      showNotification('Category name is required', 'error');
      return;
    }

    setCategoryLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        showNotification('Authentication token not found. Please login again.', 'error');
        return;
      }

      const baseUrl = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
      const url = editingCategory 
        ? `${baseUrl}/api/seo/admin/blog/categories/${editingCategory.id}/`
        : `${baseUrl}/api/seo/admin/blog/categories/create/`;
      
      const method = editingCategory ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(categoryForm)
      });

      if (response.ok) {
        showNotification(
          editingCategory ? 'Category updated successfully' : 'Category created successfully'
        );
        setShowCategoryForm(false);
        fetchCategories(); // Refresh categories list
      } else {
        const errorText = await response.text();
        console.error('Category save error:', errorText);
        
        let errorMessage = `Failed to ${editingCategory ? 'update' : 'create'} category`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        
        showNotification(errorMessage, 'error');
      }
    } catch (error) {
      console.error('Network error:', error);
      showNotification('Network error: Unable to save category', 'error');
    } finally {
      setCategoryLoading(false);
    }
  };

  const deleteBlogCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Are you sure you want to delete the category "${categoryName}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        showNotification('Authentication token not found. Please login again.', 'error');
        return;
      }

      const baseUrl = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
      const response = await fetch(`${baseUrl}/api/seo/admin/blog/categories/${categoryId}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        showNotification('Category deleted successfully');
        fetchCategories(); // Refresh categories list
      } else {
        const errorText = await response.text();
        console.error('Category delete error:', errorText);
        showNotification('Failed to delete category', 'error');
      }
    } catch (error) {
      console.error('Network error:', error);
      showNotification('Network error: Unable to delete category', 'error');
    }
  };

  // Fetch blog categories
  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      console.log('🔍 Blog - Fetching categories...');
      console.log('🔍 Blog - Token found:', token ? 'Yes' : 'No');
      
      if (!token) {
        showNotification('Authentication token not found. Please login again.', 'error');
        return;
      }

      const baseUrl = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
      const url = `${baseUrl}/api/seo/admin/blog/categories/`;
      console.log('🔍 Blog - Fetching categories from URL:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      console.log('📡 Blog - Categories response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Blog - Categories data received:', data);
        setCategories(data.categories || []);
      } else {
        const errorText = await response.text();
        console.error('❌ Blog - Failed to fetch categories:', response.status, errorText);
        
        if (response.status === 404) {
          console.log('📝 Blog - Categories endpoint not found, setting empty array');
          setCategories([]); // Set empty array if endpoint doesn't exist
        }
      }
    } catch (error) {
      console.error('❌ Blog - Error fetching categories:', error);
      setCategories([]); // Set empty array on error
    }
  };

  // Fetch blog posts
  const fetchBlogPosts = async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      
      console.log('🔍 Blog - Fetching blog posts...');
      console.log('🔍 Blog - Token found:', token ? 'Yes' : 'No');
      
      if (!token) {
        showNotification('Authentication token not found. Please login again.', 'error');
        return;
      }

      const baseUrl = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '20'
      });

      if (searchTerm) params.append('search', searchTerm);
      if (selectedCategory) params.append('category_id', selectedCategory);
      if (selectedStatus) params.append('status', selectedStatus);
      if (selectedTag) params.append('tags', selectedTag);
      if (selectedReadingTime) params.append('reading_time', selectedReadingTime);

      const url = `${baseUrl}/api/seo/admin/blog/posts/?${params}`;
      console.log('🔍 Blog - Fetching from URL:', url);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      console.log('📡 Blog - Response status:', response.status);
      console.log('📡 Blog - Response headers:', response.headers);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Blog - Data received:', data);
        setBlogPosts(data.blog_posts || []);
        setTotalPages(data.pagination?.total_pages || 1);
        setTotalItems(data.pagination?.total_items || 0);
        setCurrentPage(page);
      } else {
        const errorText = await response.text();
        console.error('❌ Blog - Failed to fetch blog posts:', response.status, errorText);
        
        // If endpoint doesn't exist, show a helpful message
        if (response.status === 404) {
          showNotification('Blog endpoints not implemented yet. This feature is coming soon!', 'error');
          setBlogPosts([]); // Set empty array to show the "no posts" state
        } else {
          showNotification('Failed to fetch blog posts', 'error');
        }
      }
    } catch (error) {
      console.error('❌ Blog - Network error:', error);
      showNotification('Network error: Unable to fetch blog posts', 'error');
      setBlogPosts([]); // Set empty array to show the "no posts" state
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchBlogPosts(1);
  };

  // Handle filter change
  const handleFilterChange = () => {
    setCurrentPage(1);
    fetchBlogPosts(1);
  };

  // Handle page change
  const handlePageChange = (page) => {
    fetchBlogPosts(page);
  };

  // Handle post selection
  const handlePostSelection = (postId) => {
    setSelectedPosts(prev => 
      prev.includes(postId) 
        ? prev.filter(id => id !== postId)
        : [...prev, postId]
    );
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedPosts.length === blogPosts.length) {
      setSelectedPosts([]);
    } else {
      setSelectedPosts(blogPosts.map(post => post.id));
    }
  };

  // Handle bulk actions
  const handleBulkAction = async (action) => {
    if (selectedPosts.length === 0) {
      showNotification('Please select posts to perform bulk action', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        showNotification('Authentication token not found. Please login again.', 'error');
        return;
      }

      const baseUrl = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
      const response = await fetch(`${baseUrl}/api/seo/admin/blog/bulk-actions/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          action: action,
          post_ids: selectedPosts
        })
      });

      if (response.ok) {
        const data = await response.json();
        showNotification(data.message);
        setSelectedPosts([]);
        fetchBlogPosts(currentPage);
      } else {
        const errorData = await response.json();
        showNotification(errorData.error || 'Bulk action failed', 'error');
      }
    } catch (error) {
      console.error('Error performing bulk action:', error);
      showNotification('Network error: Unable to perform bulk action', 'error');
    }
  };

  // Handle delete post
  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        showNotification('Authentication token not found. Please login again.', 'error');
        return;
      }

      const baseUrl = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
      const response = await fetch(`${baseUrl}/api/seo/admin/blog/posts/${postId}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        showNotification('Blog post deleted successfully');
        fetchBlogPosts(currentPage);
      } else {
        const errorData = await response.json();
        showNotification(errorData.error || 'Failed to delete blog post', 'error');
      }
    } catch (error) {
      console.error('Error deleting blog post:', error);
      showNotification('Network error: Unable to delete blog post', 'error');
    }
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Initialize data
  useEffect(() => {
    fetchCategories();
    fetchBlogPosts();
  }, []);

  // Handle filter changes
  useEffect(() => {
    if (selectedCategory !== '' || selectedStatus !== '' || selectedTag !== '' || selectedReadingTime !== '') {
      handleFilterChange();
    }
  }, [selectedCategory, selectedStatus, selectedTag, selectedReadingTime]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {onBack && (
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              {/* Back */}
            </button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Blog Management</h1>
            <p className="text-slate-600">Manage your blog posts and categories</p>
          </div>
        </div>
        <div className="flex gap-3">
           <button
            onClick={() => setShowCategoryModal(true)}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
          >
            <Tag className="w-4 h-4" />
            Manage Categories
          </button>
          <button
            onClick={() => onNavigate('blog-content', { mode: 'create' })}
            className="bg-medical-500 text-white px-4 py-2 rounded-lg hover:bg-medical-600 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Blog Post
          </button>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          className={`p-4 rounded-lg flex items-center gap-2 ${
            notification.type === 'success' 
              ? 'bg-green-100 text-green-800 border border-green-200' 
              : 'bg-red-100 text-red-800 border border-red-200'
          }`}
        >
          {notification.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          {notification.message}
        </motion.div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search blog posts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
              />
            </div>
          </form>

          {/* Filters */}
          <div className="flex gap-3 flex-wrap">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <input
              type="text"
              placeholder="Filter by tag..."
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
            />

            <select
              value={selectedReadingTime}
              onChange={(e) => setSelectedReadingTime(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
            >
              <option value="">All Reading Times</option>
              <option value="1-3">1-3 min</option>
              <option value="4-7">4-7 min</option>
              <option value="8-15">8-15 min</option>
              <option value="15+">15+ min</option>
            </select>

            <button
              type="submit"
              onClick={handleSearch}
              className="bg-slate-100 text-slate-600 px-4 py-2 rounded-lg hover:bg-slate-200 transition-colors flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              Filter
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedPosts.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-blue-50 border border-blue-200 rounded-lg p-4"
        >
          <div className="flex items-center justify-between">
            <span className="text-blue-800 font-medium">
              {selectedPosts.length} post{selectedPosts.length !== 1 ? 's' : ''} selected
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => handleBulkAction('publish')}
                className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600 transition-colors"
              >
                Publish
              </button>
              <button
                onClick={() => handleBulkAction('draft')}
                className="bg-yellow-500 text-white px-3 py-1 rounded text-sm hover:bg-yellow-600 transition-colors"
              >
                Draft
              </button>
              <button
                onClick={() => handleBulkAction('archive')}
                className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600 transition-colors"
              >
                Archive
              </button>
              <button
                onClick={() => handleBulkAction('delete')}
                className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Blog Posts Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-medical-500"></div>
          </div>
        ) : blogPosts.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No blog posts found</h3>
            <p className="text-slate-600 mb-4">Get started by creating your first blog post.</p>
            <button
              onClick={() => onNavigate('blog-content', { mode: 'create' })}
              className="bg-medical-500 text-white px-4 py-2 rounded-lg hover:bg-medical-600 transition-colors"
            >
              Create Blog Post
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-3 px-4">
                      <input
                        type="checkbox"
                        checked={selectedPosts.length === blogPosts.length && blogPosts.length > 0}
                        onChange={handleSelectAll}
                        className="rounded border-slate-300 text-medical-600 focus:ring-medical-500"
                      />
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-slate-900">Title & Content</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-900">Category</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-900">Tags</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-900">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-900">Author</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-900">Published</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-900">Reading Time</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-900">Views</th>
                    {/* <th className="text-left py-3 px-4 font-medium text-slate-900">SEO Score</th> */}
                    <th className="text-left py-3 px-4 font-medium text-slate-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {blogPosts.map((post) => (
                    <motion.tr
                      key={post.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-slate-50"
                    >
                      <td className="py-3 px-4">
                        <input
                          type="checkbox"
                          checked={selectedPosts.includes(post.id)}
                          onChange={() => handlePostSelection(post.id)}
                          className="rounded border-slate-300 text-medical-600 focus:ring-medical-500"
                        />
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          {post.featured_image && (
                            <img
                              src={post.featured_image}
                              alt={post.title || 'Untitled'}
                              className="w-12 h-12 rounded object-cover"
                            />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-slate-900 flex items-center gap-2">
                              <span className="truncate">{post.title || 'Untitled'}</span>
                              {post.is_featured && (
                                <Star className="w-4 h-4 text-yellow-500 fill-current flex-shrink-0" />
                              )}
                            </div>
                            <div className="text-sm text-slate-600 truncate max-w-xs">
                              {post.excerpt || 'No excerpt'}
                            </div>
                            {post.seo_title && post.seo_title !== post.title && (
                              <div className="text-xs text-blue-600 truncate max-w-xs">
                                SEO: {post.seo_title}
                              </div>
                            )}
                            <div className="text-xs text-slate-500">
                              Slug: {post.slug || 'No slug'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {post.category ? (
                          <span
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                            style={{
                              backgroundColor: `${post.category.color}20`,
                              color: post.category.color
                            }}
                          >
                            {post.category.name}
                          </span>
                        ) : (
                          <span className="text-slate-400">No category</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {(() => {
                          const tagsArray = getTagsArray(post.tags);
                          return tagsArray.length > 0 ? (
                            <div className="flex flex-wrap gap-1 max-w-32">
                              {tagsArray.slice(0, 3).map((tag, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700"
                                >
                                  {tag}
                                </span>
                              ))}
                              {tagsArray.length > 3 && (
                                <span className="text-xs text-slate-500">
                                  +{tagsArray.length - 3} more
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400">No tags</span>
                          );
                        })()}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(post.status || 'draft')}`}>
                          {(post.status || 'draft').charAt(0).toUpperCase() + (post.status || 'draft').slice(1)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-600">
                            {post.created_by || 'Unknown'}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-600">
                            {post.published_at ? safeFormatDate(post.published_at) : 'Not published'}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-600">
                            {post.read_time_minutes || 5} min read
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-slate-600">
                          {post.view_count || 0}
                        </span>
                      </td>
                      {/* <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${
                            post.seo_score >= 80 ? 'bg-green-500' :
                            post.seo_score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}></div>
                          <span className="text-sm text-slate-600">
                            {post.seo_score || 0}/100
                          </span>
                        </div>
                      </td> */}
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => onNavigate('blog-content', { mode: 'view', postId: post.id })}
                            className="text-slate-400 hover:text-slate-600 transition-colors"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => onNavigate('blog-content', { mode: 'edit', postId: post.id })}
                            className="text-blue-400 hover:text-blue-600 transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeletePost(post.id)}
                            className="text-red-400 hover:text-red-600 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="border-t border-slate-200 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-slate-600">
                    Showing {((currentPage - 1) * 20) + 1} to {Math.min(currentPage * 20, totalItems)} of {totalItems} posts
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const page = i + Math.max(1, currentPage - 2);
                      if (page > totalPages) return null;
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-1 text-sm border rounded ${
                            page === currentPage
                              ? 'bg-medical-500 text-white border-medical-500'
                              : 'border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Category Management Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="text-xl font-semibold text-slate-800">Manage Blog Categories</h2>
              <button
                onClick={closeCategoryModal}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Add New Category Button */}
              <div className="mb-6">
                <button
                  onClick={() => openCategoryModal()}
                  className="bg-medical-500 text-white px-4 py-2 rounded-lg hover:bg-medical-600 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add New Category
                </button>
              </div>

              {/* Categories List */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-slate-800">Existing Categories</h3>
                
                {categories.length === 0 ? (
                  <div className="text-center py-8">
                    <Tag className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">No categories found</h3>
                    <p className="text-slate-600 mb-4">Get started by creating your first blog category.</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {categories.map((category) => (
                      <div
                        key={category.id}
                        className="bg-slate-50 rounded-lg p-4 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-4">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: category.color }}
                          ></div>
                          <div>
                            <h4 className="font-medium text-slate-900">{category.name}</h4>
                            <p className="text-sm text-slate-600">
                              Slug: {category.slug} • Posts: {category.post_count || 0}
                            </p>
                            {category.description && (
                              <p className="text-sm text-slate-500 mt-1">{category.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            category.is_active 
                              ? 'text-green-800 bg-green-100' 
                              : 'text-red-800 bg-red-100'
                          }`}>
                            {category.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <button
                            onClick={() => openCategoryModal(category)}
                            className="text-blue-400 hover:text-blue-600 transition-colors p-1"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteBlogCategory(category.id, category.name)}
                            className="text-red-400 hover:text-red-600 transition-colors p-1"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Category Form Modal */}
      {showCategoryForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 60 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4"
          >
            {/* Form Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-800">
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </h3>
              <button
                onClick={closeCategoryModal}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form Content */}
            <div className="p-6 space-y-4">
              {/* Category Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={categoryForm.name}
                  onChange={(e) => handleCategoryFormChange('name', e.target.value)}
                  placeholder="Enter category name"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
                />
              </div>

              {/* Category Slug */}
              {/* <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  URL Slug
                </label>
                <input
                  type="text"
                  value={categoryForm.slug}
                  onChange={(e) => handleCategoryFormChange('slug', e.target.value)}
                  placeholder="url-friendly-slug"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
                />
                <p className="text-xs text-slate-500 mt-1">Auto-generated from name, but you can customize it</p>
              </div> */}

              {/* Category Description */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Description
                </label>
                <textarea
                  value={categoryForm.description}
                  onChange={(e) => handleCategoryFormChange('description', e.target.value)}
                  placeholder="Brief description of this category"
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
                />
              </div>

              {/* Category Color */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Category Color
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={categoryForm.color}
                    onChange={(e) => handleCategoryFormChange('color', e.target.value)}
                    className="w-12 h-10 border border-slate-200 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={categoryForm.color}
                    onChange={(e) => handleCategoryFormChange('color', e.target.value)}
                    placeholder="#3B82F6"
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Is Active */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={categoryForm.is_active}
                  onChange={(e) => handleCategoryFormChange('is_active', e.target.checked)}
                  className="rounded border-slate-300 text-medical-600 focus:ring-medical-500"
                />
                <label htmlFor="is_active" className="text-sm font-medium text-slate-700">
                  Active (visible to users)
                </label>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200">
              <button
                onClick={() => setShowCategoryForm(false)}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveBlogCategory}
                disabled={categoryLoading || !categoryForm.name.trim()}
                className="bg-medical-500 text-white px-4 py-2 rounded-lg hover:bg-medical-600 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {categoryLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {editingCategory ? 'Update Category' : 'Create Category'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}